import type { Command } from '@/core/Command';
import type { FaunaInstance } from '@/fauna/FaunaInstance';
import type { FaunaWorld } from '@/fauna/FaunaWorld';

/**
 * Undo records for placed fauna, mirroring `FloraCommands` and `SiteObjectCommands`.
 *
 * Records are snapshotted on construction and again on execute, for the reason
 * `SiteObjectCommands` spells out: the world adopts what it is given, so handing it the same
 * reference twice would let a later edit reach backwards into the undo record.
 */

function snapshot(instance: FaunaInstance): FaunaInstance {
  return { ...instance, position: { ...instance.position } };
}

/**
 * Place one creature, or a whole group, as a single step.
 *
 * One command rather than two because a flock is one gesture. Dropping fourteen goldfinches and
 * having to press undo fourteen times would make the grouping data actively unpleasant to use — see
 * `FaunaGrouping` for why placing them together is the point.
 */
export class AddFaunaCommand implements Command {
  readonly label: string;
  readonly kind = 'fauna' as const;
  private readonly instances: FaunaInstance[];

  constructor(
    private readonly world: FaunaWorld,
    instances: readonly FaunaInstance[],
    label?: string,
  ) {
    this.instances = instances.map(snapshot);
    this.label = label ?? (instances.length === 1 ? 'Place fauna' : `Place ${instances.length} fauna`);
  }

  execute(): void {
    for (const instance of this.instances) {
      if (!this.world.has(instance.id)) this.world.add(snapshot(instance));
    }
  }

  undo(): void {
    for (const instance of this.instances) this.world.remove(instance.id);
  }
}

export class RemoveFaunaCommand implements Command {
  readonly label: string;
  readonly kind = 'remove' as const;
  private readonly instances: FaunaInstance[];

  constructor(
    private readonly world: FaunaWorld,
    instances: readonly FaunaInstance[],
    label?: string,
  ) {
    this.instances = instances.map(snapshot);
    this.label =
      label ?? (instances.length === 1 ? 'Remove fauna' : `Remove ${instances.length} fauna`);
  }

  execute(): void {
    for (const instance of this.instances) this.world.remove(instance.id);
  }

  undo(): void {
    for (const instance of this.instances) {
      if (!this.world.has(instance.id)) this.world.add(snapshot(instance));
    }
  }
}

/** Move, turn, re-pose, lock, or hide a placed creature. */
export class UpdateFaunaCommand implements Command {
  readonly label: string;
  readonly kind = 'transform' as const;
  private readonly before: FaunaInstance;
  private readonly after: FaunaInstance;

  constructor(
    private readonly world: FaunaWorld,
    before: FaunaInstance,
    after: FaunaInstance,
    label = 'Adjust fauna',
  ) {
    this.before = snapshot(before);
    this.after = snapshot(after);
    this.label = label;
  }

  execute(): void {
    if (this.world.has(this.after.id)) this.world.update(snapshot(this.after));
  }

  undo(): void {
    if (this.world.has(this.before.id)) this.world.update(snapshot(this.before));
  }
}
